/**
 * A `.ds` scope for one region of a page that is still legacy.
 *
 * `DsRoot` owns a whole route: it is 100vh tall and it clips full-bleed
 * children. Converting the application happens one region at a time, inside
 * a shell that has not converted, so both of those are wrong here. This is
 * the same scope with neither.
 *
 * The theme follows the application's own light and dark setting by reading
 * the `dark` class that ThemeContext writes on <html>, rather than reading
 * that context. The class is already the single source of truth, and going
 * to it directly keeps this component working anywhere no ThemeProvider is
 * mounted, which includes every test that uses renderWithProviders.
 */
import { useSyncExternalStore, type ReactNode } from 'react'
import { cn } from '@/utils/cn'
import './tokens.css'
import './components.css'
/* `ds-tag` lives in marks.css and is emitted by EntityRow and by pages that
   write the class directly. Nothing in the application imported it, so every
   tag in settings rendered as unstyled inline text: no chip, no background,
   no radius. jsdom loads no CSS, so only looking at the page finds this.
   Importing it at the scope root means any converted region has it. */
import './marks.css'
import './island.css'

function readAppTheme(): 'light' | 'dark' {
  if (typeof document === 'undefined') return 'light'
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light'
}

function watchAppTheme(onChange: () => void): () => void {
  const observer = new MutationObserver(onChange)
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
  return () => observer.disconnect()
}

/*
  The public masthead is also built into the static pages, with no visitor
  and so in light, and taken over by React in the browser. This used to read
  the real theme on its first render. React leaves an attribute alone when it
  differs during that takeover, so the header kept the build's
  `data-ds-theme="light"` and stayed a white bar on a dark page.

  The third argument is what React uses while it takes built markup over: the
  same answer the build gave, so nothing differs. It then compares that with
  the real theme and renders again if they are not the same. A component that
  was never built reads the real theme on its first render, as before.
*/
export function useAppTheme(): 'light' | 'dark' {
  return useSyncExternalStore(watchAppTheme, readAppTheme, () => 'light')
}

/*
  For `data-ds-theme` on a scope that is built into the static pages.

  `useAppTheme` has to answer something at build time and answers light. That
  answer is then on the page, wrong for anybody in dark, until React loads.
  This one answers nothing there, so the scope is built with no theme of its
  own and takes the document's, from the class ThemeBootstrapScript writes
  before anything paints: that is the `:not([data-ds-theme])` half of every
  dark rule in the stylesheets. Once React has the page this is the real
  theme, the same as `useAppTheme`.
*/
export function useScopeTheme(): 'light' | 'dark' | undefined {
  return useSyncExternalStore<'light' | 'dark' | undefined>(watchAppTheme, readAppTheme, () => undefined)
}

export function DsIsland({
  children,
  className,
  panel = true,
  ground = true,
}: {
  children: ReactNode
  className?: string
  /** The transitional radius and padding. Off once the shell around it converts. */
  panel?: boolean
  /**
   * Whether the scope paints its own paper. Off for a region that should sit
   * directly on the page it is embedded in, like a page heading: the ink and
   * the type still come from the system, and the ground stays the host's.
   * Both grounds follow the same theme, so this is safe in light and dark.
   */
  ground?: boolean
}) {
  const theme = useAppTheme()
  return (
    <div
      className={cn('ds', panel && 'ds-island', !ground && 'ds-island--bare', className)}
      data-ds-theme={theme}
    >
      {children}
    </div>
  )
}
